/* eslint-disable no-param-reassign */
import {
  createSlice, createAsyncThunk,
} from '@reduxjs/toolkit';
import FetchStatus from 'src/types/FetchStatus';
import {
  GET_IMAGE_PRESIGNED_API, GET_IMAGE_PRESIGNED_V2_API, PROCESS_IMAGES_API, PROCESS_IMAGES_V2_API,
} from 'src/constants/apiEndpoints';
import { customFetch } from 'src/utils';
import {
  GetImagePresignedResponse, ImageResult,
  ImageUploadAPIRequest,
  ImageUploadError,
  ImageUploadRequest,
  ProcessImageAPIRequest,
} from 'src/types/ImageUpload';
import WeblabId from 'src/weblab/weblabId';
import type { AppState } from '../store';
import { getUserId, selectMarketplaceId } from './userInfo';
import { selectIsWeblabIdInT1 } from './weblab';

export interface ImageState {
  images: {
    [key: string]: {
      url: string,
      status: FetchStatus,
      error: ImageUploadError,
    }
  }
  status: FetchStatus,
}

export const initialState: ImageState = {
  images: {},
  status: FetchStatus.idle,
};

export const uploadImages = createAsyncThunk(
  'image/uploadImages',
  async (
    {
      files,
      imageType,
    }: ImageUploadRequest,
    { rejectWithValue, getState },
  ) => {
    const isThreadDetailsSSR = selectIsWeblabIdInT1(
      getState() as AppState,
      WeblabId.SELLER_FORUMS_THREAD_DETAILS_SSR,
    );
    const getPresignedURLApiPath = isThreadDetailsSSR
      ? GET_IMAGE_PRESIGNED_V2_API
      : GET_IMAGE_PRESIGNED_API;
    const imageNames = Object.keys(files);
    const getPresignedResponse = await customFetch(
      getPresignedURLApiPath,
      {
        method: 'POST',
        body: JSON.stringify({
          imageNames,
          imageType,
          marketplaceId: selectMarketplaceId(getState() as AppState),
        } as ImageUploadAPIRequest),
      },
      (error: Error) => rejectWithValue(error.message),
      0,
      true,
      null,
      (data: any, responseHeaders: Headers) => ({ data, responseHeaders }),
    );

    const { imageUrls } = getPresignedResponse.data as GetImagePresignedResponse;

    const uploadResponses = await Promise.all(imageUrls.map((presignedUrl, index) => customFetch(
      presignedUrl,
      {
        method: 'PUT',
        body: files[imageNames[index]].file,
        headers: { 'x-amz-meta-user-id': getUserId(getState() as AppState) },
        isNonJsonRequest: true,
      },
      () => ImageUploadError.presignedFailed,
      0,
      false,
      null,
    ).then((res) => {
      if (res === ImageUploadError.presignedFailed) return res;
      const { pathname } = new URL(presignedUrl);
      return pathname;
    })));

    const processImagesApiPath = isThreadDetailsSSR
      ? PROCESS_IMAGES_V2_API
      : PROCESS_IMAGES_API;
    const unvalidatedUrls = uploadResponses
      .filter((response) => response !== ImageUploadError.presignedFailed);
    const processImagesResponse = await customFetch(
      processImagesApiPath,
      {
        method: 'POST',
        body: JSON.stringify({
          unvalidatedUrls,
          imageType,
          marketplaceId: selectMarketplaceId(getState() as AppState),
        } as ProcessImageAPIRequest),
      },
      (error: Error) => rejectWithValue(error.message),
    );

    const imageResults = new Map(Object.entries(processImagesResponse.imageResults));

    const completeResponse = uploadResponses.map((uploadResponse) => {
      if (uploadResponse === ImageUploadError.presignedFailed) {
        return { errorType: uploadResponse };
      }
      const processingResult = imageResults.get(uploadResponse)! as ImageResult;
      return processingResult.valid
        ? { url: processingResult.validatedUrl }
        : { errorType: processingResult.failureCode as ImageUploadError };
    });
    return completeResponse;
  },
);

const imageSlice = createSlice({
  name: 'image',
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadImages.rejected, (state, action) => {
        const { files } = action.meta.arg;
        const imageNames = Object.keys(files);
        state.images = imageNames.reduce((acc, name) => ({
          ...acc,
          [name]: {
            status: FetchStatus.failed,
            error: ImageUploadError.presignedFailed,
          },
        }), {});
        state.status = FetchStatus.failed;
      })
      .addCase(uploadImages.pending, (state) => {
        state.status = FetchStatus.loading;
      })
      .addCase(uploadImages.fulfilled, (state, action) => {
        const { files } = action.meta.arg;
        const imageNames = Object.keys(files);
        state.images = imageNames.reduce((acc, name, index) => {
          const hasFailed = action.payload[index].errorType !== undefined;
          return ({
            ...acc,
            [name]: {
              url: hasFailed ? undefined : action.payload[index].url,
              status: hasFailed ? FetchStatus.failed : FetchStatus.complete,
              error: hasFailed ? action.payload[index].errorType as ImageUploadError : undefined,
            },
          });
        }, {});
        state.status = FetchStatus.complete;
      });
  },
});
export const { reset } = imageSlice.actions;
export default imageSlice.reducer;
